// src/components/SearchSuggestionList.tsx
import React from "react";
import { FaMapMarkerAlt, FaStar, FaEye } from "react-icons/fa";
import type { Restaurant } from "../Manager/restaurant.types";
import { ImageOff } from "lucide-react";

type Props = {
  query: string;
  isFocused: boolean;
  suggestions: Array<string | Restaurant>;
  loading?: boolean;
  selectedFilters: string[];
  onFilterToggle: (f: string) => void;
  onWordClick: (w: string) => void;
  onRestaurantClick: (r: Restaurant) => void;
  onClearFilters: () => void;
};

const SearchSuggestionList: React.FC<Props> = ({
  query,
  isFocused,
  suggestions,
  loading,
  selectedFilters,
  onFilterToggle,
  onWordClick,
  onRestaurantClick,
  onClearFilters,
}) => {
  if (!isFocused) return null;

  const availableFilters = ["Nearby","Top Rated", "Affordable", "Cuisine", "Romantic", "Cafe", "Dining", "Date"];

  // split suggestions into words and restaurants
  const words = suggestions.filter((s) => typeof s === "string") as string[];
  const restaurants = suggestions.filter((s) => typeof s !== "string") as Restaurant[];

  const generateUrl = (url: string) => {
    return `${url}?w=200&h=200&fit=crop&q=80`
  }

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#FFDDA6] rounded-xl shadow-lg max-h-[70vh] overflow-y-auto z-50">
      {/* Filters Section */}
      <div className="px-4 py-3 border-b border-gray-200 bg-[#FFF8E7]">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-[#111827] text-sm">Filters</h4>
          {selectedFilters.length > 0 && (
            <button onClick={onClearFilters} className="text-xs text-[#5E503F] hover:text-[#111827] underline">
              Clear all
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {availableFilters.map((filter) => {
            const isSelected = selectedFilters.includes(filter);
            return (
              <button
                key={filter}
                onClick={() => onFilterToggle(filter)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isSelected ? "bg-[#FFD166] text-[#111827] border-2 border-[#FFD166]" : "bg-white text-[#5E503F] border border-[#FFDDA6] hover:bg-[#FFF8E7]"
                }`}
              >
                {filter}
              </button>
            );
          })}
        </div>
      </div>

      {/* Word suggestions (simple lines) */}
      {!loading && words.length > 0 && (
        <div className="px-2 py-2 border-b border-gray-100">
          {words.map((w) => (
            <div
              key={w}
              className="px-4 py-2 hover:bg-[#FFF8E7] cursor-pointer text-sm text-[#111827]"
              onMouseDown={(e) => e.preventDefault()} // prevent blur
              onClick={() => onWordClick(w)}
            >
              {w}
            </div>
          ))}
        </div>
      )}

      {/* Restaurant results */}
      {loading ? (
        // Loading Spinner
        <div className="px-4 py-8 text-center">
          <svg
            className="animate-spin w-8 h-8 mx-auto text-[#FFD166]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            ></path>
          </svg>
          <p className="text-xs text-[#5E503F] mt-2">Loading suggestions...</p>
        </div>
      ) : restaurants.length > 0 ? (
        <>
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-semibold text-[#5E503F]">
              {restaurants.length} result{restaurants.length !== 1 ? "s" : ""} found
            </span>
          </div>
          {restaurants.map((restaurant) => (
            <div
              key={restaurant._id}
              className="px-4 py-3 hover:bg-[#FFF8E7] cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
              onMouseDown={(e) => e.preventDefault()} // keep input focus behavior consistent
              onClick={() => onRestaurantClick(restaurant)}
            >
              <div className="flex gap-3">
                {restaurant.gallery?.cover?.url ? <img
                  src={generateUrl(restaurant.gallery?.cover?.url)}
                  alt={restaurant.name}
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                /> : (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <ImageOff className="text-gray-400" size={20} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-[#111827] text-sm truncate">{restaurant.name}</h4>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <FaStar className="text-[#FFD166]" size={12} />
                      <span className="text-xs font-semibold text-[#111827]">{restaurant.rating.average}</span>
                    </div>
                  </div>
                  <div className="flex items-center text-xs  mt-1">
                    <FaMapMarkerAlt className="mr-1" size={10} />
                    <span>{restaurant.location.area}</span>
                    {restaurant.viewCount && 
                    <>
                    <span className="mx-2">•</span>
                      <FaEye className="mr-1" size={10} />
                      <span>{restaurant.viewCount || 10} views</span>
                    </>
                    }
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {restaurant.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 bg-white text-[#5E503F] text-xs rounded-full border border-[#FFDDA6]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="px-4 py-8 text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-[#5E503F] text-sm">No restaurants found</p>
          <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default SearchSuggestionList;
